import { type FormEvent, useState } from "react";
import { useNavigate } from "react-router-dom";

import AppShell, { ErrorNote } from "@/components/AppShell";
import LogModeTabs from "@/components/LogModeTabs";
import NumericInput from "@/components/NumericInput";
import { api, globalApi } from "@/lib/api";
import { toLocalInputValue, useAsync, useVesselPath } from "@/lib/hooks";
import type { ChemicalOption, DoseBatch, DoseUnit } from "@/types";

/** One row of the form. `key` is local identity so React can track rows across
 * insertion and removal — the array index cannot, because removing row 1 would
 * make row 2 inherit its state. */
interface Row {
  key: number;
  chemical: string;
  amount: string;
  unit: DoseUnit | "";
  /** Blank for chemicals sold pure, where the question does not arise. */
  strength: string;
}

let nextKey = 1;

function blankRow(chemical = ""): Row {
  return { key: nextKey++, chemical, amount: "", unit: "", strength: "" };
}

/**
 * Logging chemicals that were added — as distinct from the calculator's
 * suggestions, which log themselves with one tap.
 *
 * Several chemicals go in as one batch under a single timestamp, because that
 * is the physical event: you stand at the pool once and add what you add.
 * Logging them separately would let the parts of one application drift apart
 * on the chart.
 */
export default function LogDose() {
  const vp = useVesselPath();
  const navigate = useNavigate();
  const chemicals = useAsync<ChemicalOption[]>(
    () => globalApi.get<ChemicalOption[]>("/api/chemicals"),
    [],
  );

  const [dosedAt, setDosedAt] = useState(() => toLocalInputValue(new Date()));
  const [rows, setRows] = useState<Row[]>([blankRow()]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const options = chemicals.data ?? [];
  const optionFor = (key: string) => options.find((c) => c.key === key);

  function update(key: number, patch: Partial<Row>) {
    setRows((current) =>
      current.map((row) => {
        if (row.key !== key) return row;
        const next = { ...row, ...patch };
        // Changing chemical resets the unit: pounds are not a choice for
        // chlorine, and carrying a stale unit over would send a combination
        // the server rejects.
        if (patch.chemical !== undefined) {
          const option = optionFor(patch.chemical);
          next.unit = option?.units[0] ?? "";
          // Strength follows the chemical for the same reason the unit does:
          // 31.45% is meaningless for baking soda, and carrying it over would
          // record a figure the owner never chose.
          next.strength = option?.default_strength ?? "";
        }
        return next;
      }),
    );
  }

  const complete = rows.filter(
    (row) => row.chemical !== "" && row.amount.trim() !== "" && row.unit !== "",
  );
  const partial = rows.some(
    (row) =>
      (row.chemical !== "" || row.amount.trim() !== "") &&
      !(row.chemical !== "" && row.amount.trim() !== "" && row.unit !== ""),
  );

  async function submit(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await api.post<DoseBatch>("/api/doses/batch", {
        dosed_at: new Date(dosedAt).toISOString(),
        entries: complete.map((row) => ({
          chemical: row.chemical,
          amount: row.amount.trim(),
          unit: row.unit,
          // Omitted rather than sent blank, so the server records the strength
          // it assumed instead of storing an empty one as though it were a
          // stated fact.
          ...(row.strength.trim() ? { strength_percent: row.strength.trim() } : {}),
        })),
      });
      navigate(vp("/history"), { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not log that.");
      setSaving(false);
    }
  }

  return (
    <AppShell title="Log" subtitle="Everything you added at the same time.">
      <LogModeTabs />
      {chemicals.error && <ErrorNote message={chemicals.error} />}

      <form onSubmit={submit}>
        <label className="block">
          <span className="unit">When</span>
          <input
            type="datetime-local"
            value={dosedAt}
            onChange={(e) => setDosedAt(e.target.value)}
            className="field mt-1 w-full"
            required
          />
        </label>

        <ul className="mt-5 space-y-3">
          {rows.map((row, index) => {
            const option = optionFor(row.chemical);
            return (
              <li key={row.key} className="card p-3">
                <div className="flex items-start gap-2">
                  <label className="min-w-0 flex-1">
                    <span className="unit">Chemical</span>
                    <select
                      value={row.chemical}
                      onChange={(e) => update(row.key, { chemical: e.target.value })}
                      className="field mt-1 w-full"
                      aria-label={`Chemical for entry ${index + 1}`}
                    >
                      <option value="">Choose…</option>
                      {options.map((c) => (
                        <option key={c.key} value={c.key}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                  </label>

                  {rows.length > 1 && (
                    <button
                      type="button"
                      onClick={() => setRows((cur) => cur.filter((r) => r.key !== row.key))}
                      className="btn btn-quiet mt-5 px-3 py-2 text-sm"
                      aria-label={`Remove entry ${index + 1}`}
                    >
                      Remove
                    </button>
                  )}
                </div>

                <div className="mt-2 flex gap-2">
                  <label className="min-w-0 flex-1">
                    <span className="unit">Amount</span>
                    <NumericInput
                      value={row.amount}
                      onChange={(next) => update(row.key, { amount: next })}
                      className="field figure mt-1 w-full"
                      placeholder="0"
                      aria-label={`Amount for entry ${index + 1}`}
                    />
                  </label>
                  {option && option.strengths.length > 0 && (
                    <label className="w-28">
                      <span className="unit">Strength</span>
                      <div className="relative mt-1">
                        <NumericInput
                          list={`strengths-${row.key}`}
                          value={row.strength}
                          onChange={(next) => update(row.key, { strength: next })}
                          // pr-7 not pr-6: "12.5" plus a % sign needs the room,
                          // and a field that clips its own value reads as a bug.
                          className="field figure w-full pr-7"
                          aria-label={`Strength for entry ${index + 1}`}
                        />
                        <span className="unit pointer-events-none absolute inset-y-0 right-2 flex items-center">
                          %
                        </span>
                      </div>
                      {/* A datalist rather than a select: these cover what is
                          sold, but jugs vary and a strength you cannot type is
                          a strength you will record wrongly. */}
                      <datalist id={`strengths-${row.key}`}>
                        {option.strengths.map((value) => (
                          <option key={value} value={value} />
                        ))}
                      </datalist>
                    </label>
                  )}
                  <label className="w-28">
                    <span className="unit">Unit</span>
                    <select
                      value={row.unit}
                      onChange={(e) => update(row.key, { unit: e.target.value as DoseUnit })}
                      className="field mt-1 w-full"
                      disabled={!option}
                      aria-label={`Unit for entry ${index + 1}`}
                    >
                      {(option?.units ?? []).map((u) => (
                        <option key={u} value={u}>
                          {UNIT_LABELS[u] ?? u}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
              </li>
            );
          })}
        </ul>

        <button
          type="button"
          onClick={() => setRows((cur) => [...cur, blankRow()])}
          className="btn btn-quiet mt-3 w-full text-sm"
        >
          Add another chemical
        </button>

        {error && <ErrorNote message={error} />}

        {partial && (
          <p className="unit mt-3 text-(--color-status-warn)">
            Finish or remove the incomplete row before saving.
          </p>
        )}

        <button
          type="submit"
          disabled={saving || complete.length === 0 || partial}
          className="btn btn-primary mt-4 w-full"
        >
          {saving ? "Saving…" : `Log ${complete.length || ""} ${complete.length === 1 ? "chemical" : "chemicals"}`.trim()}
        </button>
      </form>
    </AppShell>
  );
}

/** Spelled the way a person says them. `oz` is weight and `fl oz` is volume —
 * the distinction is a factor of 16 on a powder, so it stays visible. */
const UNIT_LABELS: Record<string, string> = {
  gal: "gallons",
  fl_oz: "fl oz",
  lb: "lbs",
  oz: "oz",
  tab: "tabs",
};
